import { useSyncExternalStore } from "react";

export type InstallPrompt = Event & {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
};

type InstallState = {
  installed: boolean;
  prompt: InstallPrompt | null;
  pending: boolean;
  accepted: boolean;
  error: string | null;
};

let state: InstallState = {
  installed: false,
  prompt: null,
  pending: false,
  accepted: false,
  error: null,
};
const listeners = new Set<() => void>();
let initialized = false;

function update(patch: Partial<InstallState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function initializePwaInstall() {
  if (initialized) return;
  initialized = true;
  const standalone = window.matchMedia("(display-mode: standalone)");
  const isInstalled = () =>
    standalone.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  update({ installed: isInstalled() });
  standalone.addEventListener("change", () => update({ installed: isInstalled() }));
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    update({ prompt: event as InstallPrompt, accepted: false, error: null });
  });
  window.addEventListener("appinstalled", () => {
    update({ installed: true, prompt: null, pending: false, accepted: false });
  });
}

export async function promptPwaInstall() {
  const prompt = state.prompt;
  if (!prompt || state.pending) return;
  update({ pending: true, error: null });
  try {
    const { outcome } = await prompt.prompt();
    // O evento é de uso único. Aceitar não comprova que a instalação terminou.
    update({ prompt: null, accepted: !state.installed && outcome === "accepted" });
  } catch {
    update({
      prompt: null,
      error: "Não foi possível abrir a instalação. Use o menu do navegador abaixo.",
    });
  } finally {
    update({ pending: false });
  }
}

export function usePwaInstall() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => state,
  );
}

export function installationPlatform(
  userAgent: string,
  touchPoints = 0,
): "ios" | "android" | "desktop" {
  if (/iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && touchPoints > 1))
    return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
}
